<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { changePassword, ApiError } from '$lib/api';

  const dispatch = createEventDispatcher<{ close: void; done: void }>();

  let currentPw = '';
  let newPw = '';
  let confirmPw = '';
  let saving = false;
  let success = false;
  let fieldError: string | null = null;
  let errorMsg: string | null = null;

  function parseDetail(body: string): string | null {
    try {
      const data = JSON.parse(body);
      if (typeof data?.detail === 'string') return data.detail;
      if (Array.isArray(data?.detail) && data.detail.length > 0) {
        const first = data.detail[0];
        const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : 'request';
        return `${field}: ${first.msg || 'invalid'}`;
      }
    } catch {}
    return null;
  }

  async function handleSubmit() {
    fieldError = null;
    errorMsg = null;
    if (!currentPw) { fieldError = 'Enter your current password'; return; }
    if (!newPw) { fieldError = 'Pick a new password'; return; }
    if (newPw.length < 12) { fieldError = 'New password must be at least 12 characters'; return; }
    if (newPw !== confirmPw) { fieldError = "The new password and confirmation don't match"; return; }
    if (newPw === currentPw) { fieldError = 'New password must differ from the current one'; return; }
    saving = true;
    try {
      await changePassword(currentPw, newPw);
      success = true;
      currentPw = ''; newPw = ''; confirmPw = '';
      // Auto-close after a beat so the user sees the success state.
      setTimeout(() => dispatch('done'), 1200);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        errorMsg = 'Current password is incorrect.';
      } else if (e instanceof ApiError) {
        errorMsg = parseDetail(e.message) ?? 'Could not change password.';
      } else {
        errorMsg = 'Could not change password.';
      }
    } finally {
      saving = false;
    }
  }
</script>

<button class="backdrop" on:click={() => !saving && dispatch('close')} aria-label="Close" disabled={saving} />

<section class="modal" role="dialog" aria-modal="true" aria-label="Change password">
  <header class="head">
    <div>
      <span class="eyebrow">Account</span>
      <h2>Change password</h2>
    </div>
    <button class="close" on:click={() => dispatch('close')} aria-label="Close" disabled={saving}>✕</button>
  </header>

  {#if success}
    <div class="success-card">
      <span class="check" aria-hidden="true">✓</span>
      <div>
        <strong>Password updated.</strong>
        <p>Other devices have been signed out. This tab stays logged in.</p>
      </div>
    </div>
  {:else}
    <form on:submit|preventDefault={handleSubmit}>
      {#if errorMsg}<p class="msg msg-error">{errorMsg}</p>{/if}
      {#if fieldError}<p class="msg msg-field">{fieldError}</p>{/if}

      <label class="field">
        <span class="field-label">Current password</span>
        <input type="password" bind:value={currentPw} autocomplete="current-password" disabled={saving} />
      </label>

      <label class="field">
        <span class="field-label">New password</span>
        <input type="password" bind:value={newPw} autocomplete="new-password" disabled={saving} placeholder="At least 12 characters" />
        <span class="field-hint">A short phrase you can remember works well.</span>
      </label>

      <label class="field">
        <span class="field-label">Confirm new password</span>
        <input type="password" bind:value={confirmPw} autocomplete="new-password" disabled={saving} />
      </label>

      <div class="actions">
        <button type="button" class="btn-ghost" on:click={() => dispatch('close')} disabled={saving}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Update password'}
        </button>
      </div>
    </form>
  {/if}
</section>

<style>
  .backdrop {
    position: fixed; inset: 0; background: rgba(15, 33, 27, 0.45);
    z-index: 1100; border: none; cursor: default;
  }
  .modal {
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--paper, #F7F3EA);
    border: 1px solid var(--rule, #E2DACA);
    box-shadow: 0 18px 40px rgba(15, 33, 27, .25);
    z-index: 1101;
    padding: var(--s-5, 24px);
    color: var(--ink, #15211B);
  }
  .head {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: var(--s-3, 12px);
    margin-bottom: var(--s-4, 16px);
  }
  .head .eyebrow {
    color: var(--leaf-bright, #2D7A4F);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-weight: 600;
    display: block;
    margin-bottom: 4px;
  }
  .head h2 {
    font-family: var(--font-display, 'Fraunces', Georgia, serif);
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0;
  }
  .close {
    background: transparent; border: none;
    font-size: 1.2rem; cursor: pointer; color: var(--ink-soft, #4A524C);
    padding: 4px 10px; min-width: 36px; min-height: 36px;
  }
  .close:hover:not(:disabled) { color: var(--ink, #15211B); }
  .close:disabled { opacity: 0.4; cursor: not-allowed; }

  form { display: flex; flex-direction: column; gap: var(--s-4, 16px); }
  .field { display: flex; flex-direction: column; gap: 6px; }
  .field-label {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--ink-soft, #4A524C);
    font-weight: 600;
  }
  .field-hint { font-size: 0.78rem; color: var(--ink-muted, #7A7872); }
  .field input {
    font-family: var(--font-body, sans-serif);
    font-size: 1rem;
    padding: 12px 14px;
    background: var(--field, white);
    border: 1px solid var(--rule, #E2DACA);
    border-bottom: 2px solid var(--rule-strong, #C9C0AC);
    color: var(--ink, #15211B);
    transition: border-color 0.12s, box-shadow 0.12s;
  }
  .field input:focus {
    outline: none;
    border-color: var(--leaf-bright, #2D7A4F);
    border-bottom-color: var(--leaf-bright, #2D7A4F);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(45, 122, 79, 0.12);
  }

  .actions { display: flex; gap: var(--s-2, 8px); justify-content: flex-end; }
  .btn-primary {
    font-family: var(--font-body, sans-serif);
    padding: 10px 18px;
    background: var(--ink, #15211B); color: var(--paper, #F7F3EA);
    border: 1px solid var(--ink, #15211B);
    font-size: 0.9rem; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-primary:hover:not(:disabled) { background: var(--leaf, #1F5135); border-color: var(--leaf, #1F5135); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-ghost {
    font-family: var(--font-body, sans-serif);
    padding: 10px 18px;
    background: transparent; color: var(--ink-soft, #4A524C);
    border: 1px solid var(--rule-strong, #C9C0AC);
    font-size: 0.9rem; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-ghost:hover:not(:disabled) { background: var(--paper-soft, #FCFAF4); }
  .btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

  .msg { margin: 0; padding: 10px 14px; font-size: 0.9rem; border-left: 3px solid; }
  .msg-error {
    background: var(--alert-soft, #F5DDDD);
    border-color: var(--alert, #9B3340);
    color: var(--alert, #9B3340);
  }
  .msg-field {
    background: var(--sun-soft, #FBEBC4);
    border-color: var(--sun, #C99326);
    color: var(--ink, #15211B);
  }

  .success-card {
    display: flex; gap: var(--s-3, 12px); align-items: flex-start;
    background: var(--leaf-soft, #DCEBE0);
    border-left: 3px solid var(--leaf-bright, #2D7A4F);
    padding: var(--s-4, 16px);
    color: var(--leaf-ink, #0E2E1B);
  }
  .check {
    flex-shrink: 0;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--leaf, #1F5135);
    color: var(--paper, #F7F3EA);
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 700;
  }
  .success-card strong { display: block; font-size: 1rem; margin-bottom: 4px; }
  .success-card p { margin: 0; font-size: 0.9rem; line-height: 1.4; }
</style>
